import { useEffect, useRef, useState, useCallback } from 'react';
import {
  createChart, CrosshairMode, LineStyle,
  CandlestickSeries, LineSeries, HistogramSeries
} from 'lightweight-charts';
import { fmt, colorByValue } from '../../utils/format';
import { getTechnicals, getTechnicalsMore } from '../../utils/api';

const PERIODS = [
  { label: 'Daily',     value: '1d'  },
  { label: 'Weekly',    value: '1wk' },
  { label: 'Monthly',   value: '1mo' },
  { label: 'Quarterly', value: '3mo' },
  { label: 'Yearly',    value: '1yr' },
];

const BG   = '#161921';
const GRID = '#2a2d36';
const TEXT = '#9ca3af';

function setSeriesData(series, candles) {
  series.candle.setData(candles.map(c => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close })));
  series.ema50.setData(candles.filter(c => c.ema50 != null).map(c => ({ time: c.time, value: c.ema50 })));
  series.ema150.setData(candles.filter(c => c.ema150 != null).map(c => ({ time: c.time, value: c.ema150 })));
  series.vol.setData(candles.map(c => ({ time: c.time, value: c.volume, color: c.close >= c.open ? '#22c55e55' : '#ef444455' })));
  series.rsi.setData(candles.filter(c => c.rsi != null).map(c => ({ time: c.time, value: c.rsi })));
}

function updateSRLines(charts, srSeries, levels, candles) {
  // Remove old S/R series
  srSeries.current.forEach(s => { try { charts.price.removeSeries(s); } catch {} });
  srSeries.current = [];

  if (!candles.length || !levels) return;
  const firstTime = candles[0].time;
  const lastTime  = candles[candles.length - 1].time;

  (levels.resistance || []).forEach(price => {
    const s = charts.price.addSeries(LineSeries, {
      color: '#ef444499', lineWidth: 1, lineStyle: LineStyle.Dashed,
      priceLineVisible: false, lastValueVisible: true,
      title: `R ${fmt.currency(price)}`,
    });
    s.setData([{ time: firstTime, value: price }, { time: lastTime, value: price }]);
    srSeries.current.push(s);
  });

  (levels.support || []).forEach(price => {
    const s = charts.price.addSeries(LineSeries, {
      color: '#22c55e99', lineWidth: 1, lineStyle: LineStyle.Dashed,
      priceLineVisible: false, lastValueVisible: true,
      title: `S ${fmt.currency(price)}`,
    });
    s.setData([{ time: firstTime, value: price }, { time: lastTime, value: price }]);
    srSeries.current.push(s);
  });
}

export default function TechnicalChart({ technicals: initialTechnicals, symbol }) {
  const containerRef = useRef(null);
  const chartsRef    = useRef(null);   // { price, vol, rsi }
  const seriesRef    = useRef(null);   // { candle, ema50, ema150, vol, rsi, ob, os }
  const srSeriesRef  = useRef([]);
  const candlesRef   = useRef([]);     // full merged candle array
  const loadingMoreRef = useRef(false);
  const hasMoreRef     = useRef(true);

  const [period, setPeriod]         = useState('1d');
  const [technicals, setTechnicals] = useState(initialTechnicals);
  const [loading, setLoading]       = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // ── Build charts once ──
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    el.innerHTML = '';

    const priceH = 340, volH = 100, rsiH = 120;
    const commonOpts = (height) => ({
      height,
      layout: { background: { color: BG }, textColor: TEXT },
      grid: { vertLines: { color: GRID }, horzLines: { color: GRID } },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: { borderColor: GRID, timeVisible: true },
      rightPriceScale: { borderColor: GRID },
    });

    // Price chart
    const priceEl = document.createElement('div');
    el.appendChild(priceEl);
    const priceChart = createChart(priceEl, commonOpts(priceH));
    priceChart.timeScale().applyOptions({ visible: false });

    const candleSeries = priceChart.addSeries(CandlestickSeries, {
      upColor: '#22c55e', downColor: '#ef4444',
      borderUpColor: '#22c55e', borderDownColor: '#ef4444',
      wickUpColor: '#22c55e', wickDownColor: '#ef4444',
    });
    const ema50Series  = priceChart.addSeries(LineSeries, { color: '#3b82f6', lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });
    const ema150Series = priceChart.addSeries(LineSeries, { color: '#22c55e', lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });

    // Volume chart
    const volEl = document.createElement('div');
    volEl.style.cssText = `border-top:1px solid ${GRID};position:relative;`;
    el.appendChild(volEl);
    const volChart = createChart(volEl, commonOpts(volH));
    volChart.timeScale().applyOptions({ visible: false });
    volChart.priceScale('right').applyOptions({ scaleMargins: { top: 0.1, bottom: 0 } });
    const volSeries = volChart.addSeries(HistogramSeries, { priceFormat: { type: 'volume' } });
    const volLabel = document.createElement('div');
    volLabel.textContent = 'VOLUME';
    volLabel.style.cssText = `position:absolute;top:4px;left:8px;font-size:10px;color:${TEXT};letter-spacing:0.08em;font-weight:600;z-index:1;pointer-events:none;`;
    volEl.appendChild(volLabel);

    // RSI chart
    const rsiEl = document.createElement('div');
    rsiEl.style.cssText = `border-top:1px solid ${GRID};position:relative;`;
    el.appendChild(rsiEl);
    const rsiChart = createChart(rsiEl, commonOpts(rsiH));
    rsiChart.priceScale('right').applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } });
    const rsiSeries = rsiChart.addSeries(LineSeries, { color: '#a855f7', lineWidth: 1.5, priceLineVisible: false, lastValueVisible: true });
    const obSeries  = rsiChart.addSeries(LineSeries, { color: '#ef444488', lineWidth: 1, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: false });
    const osSeries  = rsiChart.addSeries(LineSeries, { color: '#22c55e88', lineWidth: 1, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: false });
    const rsiLabel = document.createElement('div');
    rsiLabel.textContent = 'RSI (14)';
    rsiLabel.style.cssText = `position:absolute;top:4px;left:8px;font-size:10px;color:#a855f7;letter-spacing:0.08em;font-weight:600;z-index:1;pointer-events:none;`;
    rsiEl.appendChild(rsiLabel);

    // Sync time scales
    const allCharts = [priceChart, volChart, rsiChart];
    let syncing = false;
    allCharts.forEach(src => {
      src.timeScale().subscribeVisibleLogicalRangeChange(range => {
        if (syncing || !range) return;
        syncing = true;
        allCharts.filter(c => c !== src).forEach(c => c.timeScale().setVisibleLogicalRange(range));
        syncing = false;
      });
    });

    // Resize handler
    const handleResize = () => {
      const w = el.clientWidth;
      allCharts.forEach(c => c.applyOptions({ width: w }));
    };
    window.addEventListener('resize', handleResize);

    chartsRef.current  = { price: priceChart, vol: volChart, rsi: rsiChart, allCharts };
    seriesRef.current  = { candle: candleSeries, ema50: ema50Series, ema150: ema150Series, vol: volSeries, rsi: rsiSeries, ob: obSeries, os: osSeries };
    srSeriesRef.current = [];

    return () => {
      window.removeEventListener('resize', handleResize);
      allCharts.forEach(c => c.remove());
      el.innerHTML = '';
      chartsRef.current = null;
      seriesRef.current = null;
    };
  }, []); // mount once

  // ── Push data into existing series ──
  const applyCandles = useCallback((candles, levels) => {
    if (!seriesRef.current || !chartsRef.current || !candles.length) return;
    setSeriesData(seriesRef.current, candles);

    // RSI reference lines span full range
    const firstTime = candles[0].time;
    const lastTime  = candles[candles.length - 1].time;
    seriesRef.current.ob.setData([{ time: firstTime, value: 70 }, { time: lastTime, value: 70 }]);
    seriesRef.current.os.setData([{ time: firstTime, value: 30 }, { time: lastTime, value: 30 }]);

    updateSRLines(chartsRef.current, srSeriesRef, levels, candles);
    chartsRef.current.allCharts[0].timeScale().fitContent();
  }, []);

  // ── Fetch when symbol or period changes ──
  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    hasMoreRef.current = true;
    getTechnicals(symbol, period)
      .then(data => {
        setTechnicals(data);
        candlesRef.current = data.candles || [];
        applyCandles(data.candles || [], data.levels);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [symbol, period, applyCandles]);

  // ── Infinite scroll: subscribe to left-edge pan ──
  useEffect(() => {
    if (!chartsRef.current || !symbol) return;
    const priceChart = chartsRef.current.price;

    const onRangeChange = async (range) => {
      if (!range || range.from > 10) return;          // not near left edge
      if (loadingMoreRef.current || !hasMoreRef.current) return;

      const earliest = candlesRef.current[0]?.time;
      if (!earliest) return;

      loadingMoreRef.current = true;
      setLoadingMore(true);
      try {
        const { candles: older } = await getTechnicalsMore(symbol, period, earliest);
        if (!older || older.length === 0) {
          hasMoreRef.current = false;
          return;
        }

        // Deduplicate and merge: older first, then existing
        const existingTimes = new Set(candlesRef.current.map(c => c.time));
        const newBars = older.filter(c => !existingTimes.has(c.time));
        if (newBars.length === 0) { hasMoreRef.current = false; return; }

        const merged = [...newBars, ...candlesRef.current];
        candlesRef.current = merged;

        // Update series without resetting visible range
        setSeriesData(seriesRef.current, merged);
        const firstTime = merged[0].time;
        const lastTime  = merged[merged.length - 1].time;
        seriesRef.current.ob.setData([{ time: firstTime, value: 70 }, { time: lastTime, value: 70 }]);
        seriesRef.current.os.setData([{ time: firstTime, value: 30 }, { time: lastTime, value: 30 }]);
        // Keep current viewport — don't fitContent here
      } catch (e) {
        console.error('Failed to load more candles', e);
      } finally {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    };

    priceChart.timeScale().subscribeVisibleLogicalRangeChange(onRangeChange);
    return () => {
      try { priceChart.timeScale().unsubscribeVisibleLogicalRangeChange(onRangeChange); } catch {}
    };
  }, [symbol, period]);

  if (!technicals) return null;
  const { ma50, ma150, rsi, crossSignal, priceVsMa50, priceVsMa150, trend, levels } = technicals;

  const trendColor = trend === 'uptrend' ? 'var(--green)' : trend === 'downtrend' ? 'var(--red)' : 'var(--yellow)';
  const crossColor = crossSignal === 'golden' ? 'var(--green)' : crossSignal === 'death' ? 'var(--red)' : 'var(--text-muted)';
  const rsiColor   = rsi > 70 ? 'var(--red)' : rsi < 30 ? 'var(--green)' : 'var(--text-primary)';

  return (
    <div>
      <div className="card" style={{ paddingBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
          {/* Period selector */}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 4,
                  border: `1px solid ${period === p.value ? 'var(--accent)' : 'var(--border)'}`,
                  background: period === p.value ? 'var(--accent-dim)' : 'transparent',
                  color: period === p.value ? 'var(--accent)' : 'var(--text-muted)',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >{p.label}</button>
            ))}
            {loadingMore && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>Loading older data...</span>
            )}
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11 }}>
            <LegendItem color="#3b82f6" label="EMA 50" />
            <LegendItem color="#22c55e" label="EMA 150" />
            <LegendItem color="#a855f7" label="RSI (14)" />
            <LegendItem color="#ef444499" label="Resistance" dashed />
            <LegendItem color="#22c55e99" label="Support" dashed />
          </div>
        </div>

        {/* Chart canvas */}
        <div style={{ position: 'relative' }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, background: '#161921cc', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}>
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading chart...</div>
            </div>
          )}
          <div ref={containerRef} style={{ width: '100%', height: 560, overflow: 'hidden', borderRadius: 6 }} />
        </div>
      </div>

      {/* Signals */}
      <div className="card">
        <div className="card-title">Technical Signals</div>
        <div className="grid-4">
          <div className="stat-cell"><div className="stat-label">EMA 50</div><div className="stat-value">{fmt.currency(ma50)}</div></div>
          <div className="stat-cell"><div className="stat-label">EMA 150</div><div className="stat-value">{fmt.currency(ma150)}</div></div>
          <div className="stat-cell"><div className="stat-label">RSI (14)</div><div className="stat-value" style={{ color: rsiColor }}>{rsi?.toFixed(1) || '—'}{rsi > 70 ? ' OB' : rsi < 30 ? ' OS' : ''}</div></div>
          <div className="stat-cell"><div className="stat-label">Trend</div><div className="stat-value" style={{ color: trendColor, textTransform: 'capitalize' }}>{trend}</div></div>
          <div className="stat-cell"><div className="stat-label">vs EMA 50</div><div className="stat-value" style={{ color: colorByValue(priceVsMa50) }}>{priceVsMa50 != null ? `${priceVsMa50 > 0 ? '+' : ''}${priceVsMa50}%` : '—'}</div></div>
          <div className="stat-cell"><div className="stat-label">vs EMA 150</div><div className="stat-value" style={{ color: colorByValue(priceVsMa150) }}>{priceVsMa150 != null ? `${priceVsMa150 > 0 ? '+' : ''}${priceVsMa150}%` : '—'}</div></div>
          <div className="stat-cell"><div className="stat-label">Cross Signal</div><div className="stat-value" style={{ color: crossColor, textTransform: 'capitalize' }}>{crossSignal === 'none' ? '—' : `${crossSignal} cross`}</div></div>
          <div className="stat-cell">
            <div className="stat-label">Key Levels</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>
              {(levels?.resistance || []).map(r => <div key={r} style={{ color: '#ef4444', fontFamily: 'JetBrains Mono' }}>R {fmt.currency(r)}</div>)}
              {(levels?.support || []).map(s => <div key={s} style={{ color: '#22c55e', fontFamily: 'JetBrains Mono' }}>S {fmt.currency(s)}</div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegendItem({ color, label, dashed }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <div style={{ width: 22, height: 0, borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}` }} />
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    </div>
  );
}
